import React, { useEffect, useMemo, useRef, useState } from "react";

function classNames(...xs) { return xs.filter(Boolean).join(" "); }

const AddressInput = ({ label, value, onChange, onSelect, loading, suggestions, onQuery, quickOptions }) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    function onDocClick(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("click", onDocClick);
    return () => document.removeEventListener("click", onDocClick);
  }, []);

  return (
    <div className="space-y-1" ref={containerRef}>
      <label className="text-sm font-medium text-white/80">{label}</label>
      <input
        type="text"
        value={value}
        onChange={(e) => { onChange(e.target.value); onQuery?.(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder="Start typing an address..."
        className="bw-input"
      />
      {open && (suggestions?.length || loading || (quickOptions && quickOptions.length)) ? (
        <div className="mt-1 max-h-56 overflow-auto rounded-md border border-white/10 bg-black shadow-lg">
          {quickOptions && quickOptions.length > 0 && (
            <div className="px-2 py-1 text-xs uppercase tracking-wide text-white/60">Shortcuts</div>
          )}
          {quickOptions?.map((s) => (
            <button
              key={`quick-${s.id}`}
              type="button"
              onClick={() => { onSelect(s.name); setOpen(false); }}
              className="block w-full cursor-pointer truncate px-3 py-2 text-left text-sm text-white hover:bg-white/10"
              title={s.name}
            >
              {s.prefix ? <span className="text-white/60 mr-1">{s.prefix}:</span> : null}{s.name}
            </button>
          ))}

          {loading && (
            <div className="px-3 py-2 text-sm bw-muted">Searching...</div>
          )}
          {suggestions?.map((s) => (
            <button
              key={s.id || s.name}
              type="button"
              onClick={() => { onSelect(s.name); setOpen(false); }}
              className="block w-full cursor-pointer truncate px-3 py-2 text-left text-sm text-white hover:bg-white/10"
              title={s.name}
            >
              {s.name}
            </button>
          ))}
          {!loading && (!suggestions || suggestions.length === 0) && (!quickOptions || quickOptions.length === 0) && (
            <div className="px-3 py-2 text-sm bw-muted">No suggestions</div>
          )}
        </div>
      ) : null}
    </div>
  );
};

const RequestRideModal = ({ open, onClose, onSubmit, submitting, defaultBid = 0, profile, apiError }) => {
  const [pickup, setPickup] = useState("");
  const [dropoff, setDropoff] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [bid, setBid] = useState(String(defaultBid || 0));
  const [notes, setNotes] = useState("");

  const [errors, setErrors] = useState({});

  // capture optional geolocation to bias address suggestions
  const [geo, setGeo] = useState(null);

  // Home/Work quick picks
  const quickOptions = useMemo(() => {
    const out = [];
    const home = profile?.homeAddress?.trim?.();
    const work = profile?.workAddress?.trim?.();
    if (home) out.push({ id: 'home', name: home, prefix: 'Home' });
    if (work) out.push({ id: 'work', name: work, prefix: 'Work' });
    return out;
  }, [profile]);

  // suggestions
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSuggest, setLoadingSuggest] = useState(false);
  const suggestTimer = useRef(null);

  useEffect(() => {
    if (!open) {
      setPickup("");
      setDropoff("");
      setDate("");
      setTime("");
      setBid(String(defaultBid || 0));
      setNotes("");
      setErrors({});
      setSuggestions([]);
      return; // don't request geolocation when closed
    }

    // Try to get user location (best-effort). If permission denied, continue without it.
    if (navigator?.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const { latitude, longitude, accuracy } = pos.coords || {};
          if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
            setGeo({ lat: latitude, lon: longitude, accuracy });
          }
        },
        () => {},
        { enableHighAccuracy: true, timeout: 6000, maximumAge: 120000 }
      );
    }
  }, [open, defaultBid]);

  const querySuggest = (q) => {
    if (!q || q.length < 3) {
      setSuggestions([]);
      return;
    }
    window.clearTimeout(suggestTimer.current);
    suggestTimer.current = window.setTimeout(async () => {
      try {
        setLoadingSuggest(true);
        const base = process.env.REACT_APP_MINIAPP_API_BASE_URL || "/telegramMiniApp";
        const params = new URLSearchParams({ q });
        if (geo?.lat && geo?.lon) {
          params.set("lat", String(geo.lat));
          params.set("lon", String(geo.lon));
        }
        const resp = await fetch(`${base}/suggest?${params.toString()}`);
        const data = await resp.json();
        if (data?.success) {
          setSuggestions(data.data || []);
        } else {
          setSuggestions([]);
        }
      } catch (e) {
        setSuggestions([]);
      } finally {
        setLoadingSuggest(false);
      }
    }, 250);
  };

  const validate = () => {
    const errs = {};
    if (!pickup.trim()) errs.pickup = "Pickup is required";
    if (!dropoff.trim()) errs.dropoff = "Drop-off is required";
    if (!date) errs.date = "Date is required";
    if (!time) errs.time = "Time is required";
    if (pickup && dropoff && pickup.trim().toLowerCase() === dropoff.trim().toLowerCase()) {
      errs.dropoff = "Pickup and drop-off cannot be the same";
    }
    const bidNum = Number(bid);
    if (Number.isNaN(bidNum) || bidNum < 0) {
      errs.bid = "Bid must be a non-negative number";
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    // Combine date and time into ISO string for server parsing
    const rideTime = new Date(`${date}T${time}:00`);
    if (isNaN(rideTime.getTime())) {
      setErrors((prev) => ({ ...prev, time: "Invalid date/time" }));
      return;
    }

    await onSubmit({ pickup, dropoff, rideTime: rideTime.toISOString(), bid: Number(bid) || 0, notes });
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4">
      <div className="w-full max-w-lg rounded-xl border border-white/10 bg-neutral-950 p-5 shadow-2xl shadow-black/60">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white">Request a ride</h2>
          <button onClick={onClose} className="bw-btn-ghost">✕</button>
        </div>

        {apiError ? (
          <div className="mb-3 rounded-md border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-300">
            {apiError}
          </div>
        ) : null}

        <form onSubmit={handleSubmit} className="space-y-3">
          <AddressInput
            label="Pickup location"
            value={pickup}
            onChange={setPickup}
            onSelect={setPickup}
            suggestions={suggestions}
            loading={loadingSuggest}
            onQuery={querySuggest}
            quickOptions={quickOptions}
          />
          {errors.pickup && <p className="text-xs text-white/80 italic">{errors.pickup}</p>}

          <AddressInput
            label="Drop-off location"
            value={dropoff}
            onChange={setDropoff}
            onSelect={setDropoff}
            suggestions={suggestions}
            loading={loadingSuggest}
            onQuery={querySuggest}
            quickOptions={quickOptions}
          />
          {errors.dropoff && <p className="text-xs text-white/80 italic">{errors.dropoff}</p>}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-white/80">Date</label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="bw-input mt-1"
              />
              {errors.date && <p className="text-xs text-white/80 italic">{errors.date}</p>}
            </div>
            <div>
              <label className="text-sm font-medium text-white/80">Time</label>
              <input
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                className="bw-input mt-1"
              />
              {errors.time && <p className="text-xs text-white/80 italic">{errors.time}</p>}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-white/80">Bid (optional)</label>
              <input
                type="number"
                inputMode="decimal"
                min="0"
                step="1"
                value={bid}
                onChange={(e) => setBid(e.target.value)}
                className="bw-input mt-1"
              />
              {errors.bid && <p className="text-xs text-white/80 italic">{errors.bid}</p>}
            </div>
            <div>
              <label className="text-sm font-medium text-white/80">Notes (optional)</label>
              <input
                type="text"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any instructions for driver"
                className="bw-input mt-1"
              />
            </div>
          </div>

          <div className="mt-4 flex items-center justify-end gap-2">
            <button type="button" onClick={onClose} className="bw-btn-ghost">Cancel</button>
            <button type="submit" disabled={submitting} className={classNames("bw-btn", submitting && "opacity-50")}>{submitting ? "Requesting..." : "Request Ride"}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RequestRideModal;
